/**
 * WebhookGuideModal
 *
 * Portal-based modal displaying the Zoho Projects webhook setup guide.
 * Mirrors the ChartModal portal pattern: createPortal to document.body,
 * Escape key + backdrop click to close, body scroll lock.
 *
 * Guide content is hardcoded as JSX — no runtime file loading needed.
 * Each code block has a copy-to-clipboard button.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";

interface WebhookGuideModalProps {
	onClose: () => void;
}

/* -----------------------------------------------------------------------
   Individual code block with copy-to-clipboard
   ----------------------------------------------------------------------- */

interface CodeBlockProps {
	code: string;
	language?: string;
}

function CodeBlock({ code, language = "deluge" }: CodeBlockProps) {
	const [copied, setCopied] = useState(false);

	const handleCopy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(code.trim());
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch {
			// Fallback for browsers without clipboard API
			const el = document.createElement("textarea");
			el.value = code.trim();
			el.style.position = "fixed";
			el.style.opacity = "0";
			document.body.appendChild(el);
			el.select();
			document.execCommand("copy");
			document.body.removeChild(el);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	}, [code]);

	return (
		<div className="wg-code-block">
			<div className="wg-code-block-header">
				<span className="wg-code-lang">{language}</span>
				<button
					className={`wg-copy-btn${copied ? " wg-copy-btn--copied" : ""}`}
					onClick={handleCopy}
					type="button"
					aria-label={copied ? "Copied to clipboard" : "Copy code to clipboard"}
				>
					{copied ? (
						<>
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
								<path d="M2 6.5L4.5 9L10 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
							Copied
						</>
					) : (
						<>
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
								<rect x="4" y="4" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.25" />
								<path d="M8 4V2.5A1.5 1.5 0 0 0 6.5 1H2.5A1.5 1.5 0 0 0 1 2.5V6.5A1.5 1.5 0 0 0 2.5 8H4" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
							</svg>
							Copy
						</>
					)}
				</button>
			</div>
			<pre className="wg-code-pre"><code>{code.trim()}</code></pre>
		</div>
	);
}

/* -----------------------------------------------------------------------
   Table helper
   ----------------------------------------------------------------------- */

interface TableProps {
	headers: string[];
	rows: string[][];
}

function GuideTable({ headers, rows }: TableProps) {
	return (
		<div className="wg-table-wrapper">
			<table className="wg-table">
				<thead>
					<tr>
						{headers.map((h) => <th key={h}>{h}</th>)}
					</tr>
				</thead>
				<tbody>
					{rows.map((row, i) => (
						<tr key={i}>
							{row.map((cell, j) => (
								<td key={j} dangerouslySetInnerHTML={{ __html: cell }} />
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

/* -----------------------------------------------------------------------
   Deluge code samples — defined once, referenced below
   ----------------------------------------------------------------------- */

const CODE_TASK_SYNC = `
// PIA Webhook — Task Sync
// Sends a notification to PIA when a task is created or updated.
//
// Configuration (update these values for your deployment)
webhookUrl = "https://pia-worker.caldere.workers.dev/api/webhooks/project-entity";
bearerToken = "YOUR_WEBHOOK_SECRET_HERE";
zohoUserId = "YOUR_ZOHO_USER_ID_HERE";
portalName = "YOUR_PORTAL_NAME_HERE"; // The portal slug visible in your Zoho Projects URL, e.g. "mycompany"

// Build payload
// entity_id and project_id are implicit arguments from the automation rule
payload = Map();
payload.put("event_type", "entity_updated");
payload.put("entity_type", "task_description");
payload.put("entity_id", entity_id.toString());
payload.put("project_id", project_id.toString());
payload.put("portal_name", portalName);
payload.put("timestamp", zoho.currenttime.toString("yyyy-MM-dd'T'HH:mm:ss.SSS'Z'", "UTC"));
payload.put("zoho_user_id", zohoUserId);

// POST to PIA webhook
try
{
    headers = Map();
    headers.put("Authorization", "Bearer " + bearerToken);
    headers.put("Content-Type", "application/json");

    response = invokeUrl
    [
        url: webhookUrl
        type: POST
        headers: headers
        body: payload.toString()
    ];
    info "PIA webhook response: " + response;
}
catch (e)
{
    // Log but don't fail — automation must continue even if PIA is unreachable
    info "PIA webhook failed: " + e.toString();
}
`;

const CODE_BUG_SYNC = `
// PIA Webhook — Bug/Issue Sync
// Sends a notification to PIA when a bug is created or updated.
//
// Configuration (update these values for your deployment)
webhookUrl = "https://pia-worker.caldere.workers.dev/api/webhooks/project-entity";
bearerToken = "YOUR_WEBHOOK_SECRET_HERE";
zohoUserId = "YOUR_ZOHO_USER_ID_HERE";
portalName = "YOUR_PORTAL_NAME_HERE"; // The portal slug visible in your Zoho Projects URL, e.g. "mycompany"

// Build payload
payload = Map();
payload.put("event_type", "entity_updated");
payload.put("entity_type", "bug");
payload.put("entity_id", entity_id.toString());
payload.put("project_id", project_id.toString());
payload.put("portal_name", portalName);
payload.put("timestamp", zoho.currenttime.toString("yyyy-MM-dd'T'HH:mm:ss.SSS'Z'", "UTC"));
payload.put("zoho_user_id", zohoUserId);

try
{
    headers = Map();
    headers.put("Authorization", "Bearer " + bearerToken);
    headers.put("Content-Type", "application/json");

    response = invokeUrl
    [
        url: webhookUrl
        type: POST
        headers: headers
        body: payload.toString()
    ];
    info "PIA webhook response: " + response;
}
catch (e)
{
    info "PIA webhook failed: " + e.toString();
}
`;

const CODE_TASK_COMMENT_SYNC = `
// PIA Webhook — Task Comment Sync
// Sends a notification to PIA when a comment is added to a task.
// entity_id here is the task ID (parent), not the comment ID.
//
// Configuration (update these values for your deployment)
webhookUrl = "https://pia-worker.caldere.workers.dev/api/webhooks/project-entity";
bearerToken = "YOUR_WEBHOOK_SECRET_HERE";
zohoUserId = "YOUR_ZOHO_USER_ID_HERE";
portalName = "YOUR_PORTAL_NAME_HERE"; // The portal slug visible in your Zoho Projects URL, e.g. "mycompany"

// Build payload
payload = Map();
payload.put("event_type", "entity_updated");
payload.put("entity_type", "task_comment");
payload.put("entity_id", entity_id.toString());
payload.put("project_id", project_id.toString());
payload.put("portal_name", portalName);
payload.put("timestamp", zoho.currenttime.toString("yyyy-MM-dd'T'HH:mm:ss.SSS'Z'", "UTC"));
payload.put("zoho_user_id", zohoUserId);

try
{
    headers = Map();
    headers.put("Authorization", "Bearer " + bearerToken);
    headers.put("Content-Type", "application/json");

    response = invokeUrl
    [
        url: webhookUrl
        type: POST
        headers: headers
        body: payload.toString()
    ];
    info "PIA webhook response: " + response;
}
catch (e)
{
    info "PIA webhook failed: " + e.toString();
}
`;

const CODE_BUG_COMMENT_SYNC = `
// PIA Webhook — Bug Comment Sync
// Sends a notification to PIA when a comment is added to a bug.
// entity_id here is the bug ID (parent), not the comment ID.
//
// Configuration (update these values for your deployment)
webhookUrl = "https://pia-worker.caldere.workers.dev/api/webhooks/project-entity";
bearerToken = "YOUR_WEBHOOK_SECRET_HERE";
zohoUserId = "YOUR_ZOHO_USER_ID_HERE";
portalName = "YOUR_PORTAL_NAME_HERE"; // The portal slug visible in your Zoho Projects URL, e.g. "mycompany"

// Build payload
payload = Map();
payload.put("event_type", "entity_updated");
payload.put("entity_type", "bug_comment");
payload.put("entity_id", entity_id.toString());
payload.put("project_id", project_id.toString());
payload.put("portal_name", portalName);
payload.put("timestamp", zoho.currenttime.toString("yyyy-MM-dd'T'HH:mm:ss.SSS'Z'", "UTC"));
payload.put("zoho_user_id", zohoUserId);

try
{
    headers = Map();
    headers.put("Authorization", "Bearer " + bearerToken);
    headers.put("Content-Type", "application/json");

    response = invokeUrl
    [
        url: webhookUrl
        type: POST
        headers: headers
        body: payload.toString()
    ];
    info "PIA webhook response: " + response;
}
catch (e)
{
    info "PIA webhook failed: " + e.toString();
}
`;

const CODE_FORUM_SYNC = `
// PIA Webhook — Forum Post Sync
// Sends a notification to PIA when a forum topic is created or updated.
//
// Configuration (update these values for your deployment)
webhookUrl = "https://pia-worker.caldere.workers.dev/api/webhooks/project-entity";
bearerToken = "YOUR_WEBHOOK_SECRET_HERE";
zohoUserId = "YOUR_ZOHO_USER_ID_HERE";
portalName = "YOUR_PORTAL_NAME_HERE"; // The portal slug visible in your Zoho Projects URL, e.g. "mycompany"

// Build payload
payload = Map();
payload.put("event_type", "entity_updated");
payload.put("entity_type", "forum_post");
payload.put("entity_id", entity_id.toString());
payload.put("project_id", project_id.toString());
payload.put("portal_name", portalName);
payload.put("timestamp", zoho.currenttime.toString("yyyy-MM-dd'T'HH:mm:ss.SSS'Z'", "UTC"));
payload.put("zoho_user_id", zohoUserId);

try
{
    headers = Map();
    headers.put("Authorization", "Bearer " + bearerToken);
    headers.put("Content-Type", "application/json");

    response = invokeUrl
    [
        url: webhookUrl
        type: POST
        headers: headers
        body: payload.toString()
    ];
    info "PIA webhook response: " + response;
}
catch (e)
{
    info "PIA webhook failed: " + e.toString();
}
`;

const CODE_WRANGLER_TAIL = `npx wrangler tail`;

const CODE_WRANGLER_TAIL_OUTPUT = `[webhook] Accepted: task_description:12345678 in project 98765432 (call #1, user your-zoho-user-id)
[webhook] Background sync complete: task_description:12345678`;

const CODE_BUDGET_RESPONSE = `{
  "date": "2026-02-26",
  "callsToday": 1,
  "dailyLimit": 2000,
  "percentUsed": 0.05,
  "status": "normal"
}`;

const CODE_OPENSSL = `openssl rand -base64 32`;

const CODE_WRANGLER_SECRET = `npx wrangler secret put WEBHOOK_SECRET
# Paste the new value when prompted`;

const CODE_PAYLOAD_SCHEMA = `{
  "event_type": "entity_updated",
  "entity_type": "task_description",
  "entity_id": "12345678",
  "project_id": "98765432",
  "portal_name": "your-portal-slug",
  "timestamp": "2026-02-26T14:30:00.000Z",
  "zoho_user_id": "your-zoho-user-id"
}`;

/* -----------------------------------------------------------------------
   Architecture diagram as styled ASCII
   ----------------------------------------------------------------------- */

const CODE_ARCHITECTURE = `Zoho Projects (task/bug/comment created or edited)
    |
    v
Automation Rule (workflow trigger)
    |
    v
Deluge Custom Function (invokeurl POST)
    |
    v
Cloudflare Worker: POST /api/webhooks/project-entity
    |  - Bearer token authentication
    |  - Validates payload
    |  - Increments daily call counter (KV)
    |  - Returns 202 Accepted immediately
    |
    v
Background sync (ctx.waitUntil)
    |  - Fetches full entity from Zoho Projects API
    |  - Chunks and embeds content
    |  - Upserts into pgvector (project_entities + entity_chunks)
    |
    v
Available in PIA chat (RAG retrieval)`;

/* -----------------------------------------------------------------------
   Main component
   ----------------------------------------------------------------------- */

export default function WebhookGuideModal({ onClose }: WebhookGuideModalProps) {
	const modalRef = useRef<HTMLDivElement>(null);
	const closeButtonRef = useRef<HTMLButtonElement>(null);

	// Escape key to close
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [onClose]);

	// Focus the close button on open
	useEffect(() => {
		closeButtonRef.current?.focus();
	}, []);

	// Prevent body scroll while open
	useEffect(() => {
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, []);

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) onClose();
	};

	const modalContent = (
		<div
			className="wg-backdrop"
			onClick={handleBackdropClick}
			role="dialog"
			aria-modal="true"
			aria-label="Webhook Setup Guide"
		>
			<div className="wg-panel" ref={modalRef}>
				{/* Header */}
				<div className="wg-header">
					<div className="wg-header-title-group">
						<h2 className="wg-title">Webhook Setup Guide</h2>
						<p className="wg-subtitle">Zoho Projects → PIA real-time sync via Deluge custom functions</p>
					</div>
					<button
						ref={closeButtonRef}
						className="wg-close-btn"
						onClick={onClose}
						type="button"
						aria-label="Close guide"
					>
						<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
							<path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
						</svg>
					</button>
				</div>

				{/* Scrollable body */}
				<div className="wg-body">
					<div className="wg-content">

						{/* Section 1 — How It Works */}
						<section className="wg-section">
							<h3 className="wg-section-heading">
								<span className="wg-section-num">1</span>
								How It Works
							</h3>
							<p className="wg-para">
								The custom function acts as a lightweight relay. It does not fetch or process data
								itself — it simply notifies PIA that something changed. PIA then uses its own
								Zoho OAuth tokens to fetch the full entity and embed it.
							</p>
							<CodeBlock code={CODE_ARCHITECTURE} language="architecture" />
						</section>

						{/* Section 2 — Prerequisites */}
						<section className="wg-section">
							<h3 className="wg-section-heading">
								<span className="wg-section-num">2</span>
								Prerequisites
							</h3>
							<ul className="wg-list">
								<li><strong>PIA deployed</strong> — Worker running at <code className="wg-inline-code">https://pia-worker.caldere.workers.dev</code></li>
								<li><strong>Project registered</strong> — The Zoho Projects project must be registered in PIA (visible in the project list)</li>
								<li><strong>Ingestion user authenticated</strong> — The user whose ID is in the custom function must have an active OAuth session in PIA (log in via browser at least once)</li>
								<li><strong>Admin access</strong> to Zoho Projects (Settings &gt; Developer Space requires admin or project owner role)</li>
							</ul>
						</section>

						{/* Section 3 — Custom Functions */}
						<section className="wg-section">
							<h3 className="wg-section-heading">
								<span className="wg-section-num">3</span>
								Step 1: Create the Custom Functions
							</h3>
							<p className="wg-para">
								Navigate to: <strong>Zoho Projects &gt; Settings (gear icon) &gt; Developer Space &gt; Custom Functions</strong>
							</p>
							<p className="wg-para">
								Zoho Projects separates custom functions by module. Create functions under the relevant module tabs (Tasks, Issues, etc.).
							</p>

							<h4 className="wg-subsection-heading">3.1 Task Sync Function</h4>
							<p className="wg-para">
								<strong>Module:</strong> Tasks &nbsp;·&nbsp; <strong>Function name:</strong> <code className="wg-inline-code">piaSyncTask</code>
							</p>
							<CodeBlock code={CODE_TASK_SYNC} language="deluge" />

							<h4 className="wg-subsection-heading">3.2 Bug/Issue Sync Function</h4>
							<p className="wg-para">
								<strong>Module:</strong> Issues (Bugs) &nbsp;·&nbsp; <strong>Function name:</strong> <code className="wg-inline-code">piaSyncBug</code>
							</p>
							<CodeBlock code={CODE_BUG_SYNC} language="deluge" />

							<h4 className="wg-subsection-heading">3.3 Task Comment Sync Function</h4>
							<p className="wg-para">
								<strong>Module:</strong> Tasks &nbsp;·&nbsp; <strong>Function name:</strong> <code className="wg-inline-code">piaSyncTaskComment</code>
							</p>
							<CodeBlock code={CODE_TASK_COMMENT_SYNC} language="deluge" />

							<h4 className="wg-subsection-heading">3.4 Bug Comment Sync Function</h4>
							<p className="wg-para">
								<strong>Module:</strong> Issues (Bugs) &nbsp;·&nbsp; <strong>Function name:</strong> <code className="wg-inline-code">piaSyncBugComment</code>
							</p>
							<CodeBlock code={CODE_BUG_COMMENT_SYNC} language="deluge" />

							<h4 className="wg-subsection-heading">3.5 Forum Post Sync Function</h4>
							<p className="wg-para">
								<strong>Module:</strong> Forums (if automation rules are available for this module) &nbsp;·&nbsp; <strong>Function name:</strong> <code className="wg-inline-code">piaSyncForumPost</code>
							</p>
							<CodeBlock code={CODE_FORUM_SYNC} language="deluge" />
						</section>

						{/* Section 4 — Automation Rules */}
						<section className="wg-section">
							<h3 className="wg-section-heading">
								<span className="wg-section-num">4</span>
								Step 2: Create Automation Rules
							</h3>
							<p className="wg-para">
								Navigate to: <strong>Zoho Projects &gt; Settings &gt; Automation &gt; Workflow Rules</strong>
							</p>
							<p className="wg-para">Create rules for each module. Each rule triggers the corresponding custom function from Step 1.</p>

							<h4 className="wg-subsection-heading">4.1 Task Rule</h4>
							<GuideTable
								headers={["Field", "Value"]}
								rows={[
									["Rule name", "PIA Sync — Tasks"],
									["Module", "Tasks"],
									["Trigger", "When a task is <strong>Created</strong> or <strong>Edited</strong>"],
									["Criteria", "(none — apply to all tasks)"],
									["Action", "Execute custom function: <code>piaSyncTask</code>"],
								]}
							/>

							<h4 className="wg-subsection-heading">4.2 Bug/Issue Rule</h4>
							<GuideTable
								headers={["Field", "Value"]}
								rows={[
									["Rule name", "PIA Sync — Bugs"],
									["Module", "Issues (Bugs)"],
									["Trigger", "When a bug is <strong>Created</strong> or <strong>Edited</strong>"],
									["Criteria", "(none — apply to all bugs)"],
									["Action", "Execute custom function: <code>piaSyncBug</code>"],
								]}
							/>

							<h4 className="wg-subsection-heading">4.3 Task Comment Rule</h4>
							<GuideTable
								headers={["Field", "Value"]}
								rows={[
									["Rule name", "PIA Sync — Task Comments"],
									["Module", "Tasks"],
									["Trigger", "When a <strong>Comment is Added</strong> (if available as trigger)"],
									["Criteria", "(none)"],
									["Action", "Execute custom function: <code>piaSyncTaskComment</code>"],
								]}
							/>
							<p className="wg-callout">
								If "Comment Added" is not available as a workflow trigger for Tasks, task comments will be synced via the nightly cron and manual sync instead.
							</p>

							<h4 className="wg-subsection-heading">4.4 Bug Comment Rule</h4>
							<GuideTable
								headers={["Field", "Value"]}
								rows={[
									["Rule name", "PIA Sync — Bug Comments"],
									["Module", "Issues (Bugs)"],
									["Trigger", "When a <strong>Comment is Added</strong> (if available as trigger)"],
									["Criteria", "(none)"],
									["Action", "Execute custom function: <code>piaSyncBugComment</code>"],
								]}
							/>
							<p className="wg-callout">
								Same caveat as task comments — if unavailable as a trigger, bug comments fall back to cron/manual sync.
							</p>

							<h4 className="wg-subsection-heading">4.5 Forum Post Rule</h4>
							<GuideTable
								headers={["Field", "Value"]}
								rows={[
									["Rule name", "PIA Sync — Forum Posts"],
									["Module", "Forums"],
									["Trigger", "When a forum topic is <strong>Created</strong> or <strong>Edited</strong>"],
									["Criteria", "(none)"],
									["Action", "Execute custom function: <code>piaSyncForumPost</code>"],
								]}
							/>
							<p className="wg-callout">
								Forum automation may not be available in all Zoho Projects plans. If unavailable, forum posts are synced via cron/manual sync only.
							</p>
						</section>

						{/* Section 5 — Verify */}
						<section className="wg-section">
							<h3 className="wg-section-heading">
								<span className="wg-section-num">5</span>
								Step 3: Verify It Works
							</h3>
							<ol className="wg-list wg-list--ordered">
								<li>
									<strong>Create or edit a task</strong> in the Zoho Projects project that is registered in PIA.
								</li>
								<li>
									<strong>Check Worker logs</strong> — run in a terminal:
									<CodeBlock code={CODE_WRANGLER_TAIL} language="bash" />
									You should see log lines like:
									<CodeBlock code={CODE_WRANGLER_TAIL_OUTPUT} language="log" />
								</li>
								<li>
									<strong>Check the call budget</strong> — open browser dev tools → Network tab, then navigate to{" "}
									<code className="wg-inline-code">https://pia-worker.caldere.workers.dev/api/webhooks/budget</code>{" "}
									whilst logged in to PIA, or check the budget indicator in the PIA settings panel.
									<CodeBlock code={CODE_BUDGET_RESPONSE} language="json" />
								</li>
								<li>
									<strong>Ask PIA a question</strong> that would use the task data. RAG retrieval is based on
									semantic similarity, not recency — a question like "what decisions were made about the
									homepage design?" will retrieve the relevant task more reliably than "what was updated today?"
								</li>
							</ol>
						</section>

						{/* Section 6 — Call Budget */}
						<section className="wg-section">
							<h3 className="wg-section-heading">
								<span className="wg-section-num">6</span>
								Call Budget
							</h3>
							<p className="wg-para">
								Zoho Projects imposes a daily <code className="wg-inline-code">invokeurl</code> call limit of{" "}
								<strong>2,000 calls per organisation</strong>.
							</p>

							<h4 className="wg-subsection-heading">Budget tracking</h4>
							<ul className="wg-list">
								<li><strong>Normal</strong> (&lt; 80%): All calls processed normally</li>
								<li><strong>Warning</strong> (80–99%): Calls still processed, but the budget endpoint returns <code className="wg-inline-code">"status": "warning"</code></li>
								<li><strong>Critical</strong> (&ge; 100%): Zoho itself blocks further <code className="wg-inline-code">invokeurl</code> calls from Deluge</li>
							</ul>

							<h4 className="wg-subsection-heading">Estimated usage</h4>
							<GuideTable
								headers={["Scenario", "Estimated daily calls"]}
								rows={[
									["Light usage (1–2 active projects)", "10–30"],
									["Normal usage (5 active projects)", "50–150"],
									["Heavy usage (10+ active projects)", "200–500"],
									["Safe capacity", "~15–20 active projects"],
								]}
							/>

							<h4 className="wg-subsection-heading">Reducing call volume</h4>
							<p className="wg-para">If approaching the limit:</p>
							<ul className="wg-list">
								<li>Remove automation rules from low-priority projects (rely on cron sync instead)</li>
								<li>Narrow trigger criteria (e.g. only sync tasks in specific task lists)</li>
								<li>Reduce "Edited" triggers to "Created" only for less critical modules</li>
							</ul>
						</section>

						{/* Section 7 — Troubleshooting */}
						<section className="wg-section">
							<h3 className="wg-section-heading">
								<span className="wg-section-num">7</span>
								Troubleshooting
							</h3>
							<GuideTable
								headers={["Problem", "Likely Cause", "Fix"]}
								rows={[
									["Webhook not firing", "Automation rule disabled or misconfigured", "Check Settings &gt; Automation &gt; Workflow Rules — ensure rule is active and criteria match"],
									["<code>invokeUrl</code> error in Deluge logs", "Worker unreachable or URL typo", "Verify <code>webhookUrl</code> in the custom function. Check Worker is deployed (<code>npx wrangler deploy</code>)"],
									["PIA returns <strong>401 Unauthorised</strong>", "Bearer token mismatch", "Compare <code>bearerToken</code> in Deluge function with <code>WEBHOOK_SECRET</code> in Cloudflare. Or the ingestion user's OAuth has expired — log in via PIA browser to refresh"],
									["PIA returns <strong>404 Not Found</strong>", "Project not registered in PIA", "Open PIA, ensure the project appears in the project list"],
									["PIA returns <strong>400 Bad Request</strong>", "Payload missing required fields", "Check the Deluge function builds all required fields: <code>project_id</code>, <code>entity_type</code>, <code>entity_id</code>, <code>zoho_user_id</code>"],
									["Entity not appearing in chat", "Background sync may have failed silently", "Check Worker logs (<code>npx wrangler tail</code>). Use manual sync as fallback (Settings &gt; Sync Project Data)"],
									["Budget shows \"critical\"", "Daily <code>invokeurl</code> limit reached", "Wait until midnight UTC for reset. Nightly cron will catch up on missed entities"],
									["Deluge execution log shows timeout", "Worker responded too slowly", "The Worker returns 202 within milliseconds. If Deluge still times out, check for DNS/network issues"],
								]}
							/>

							<h4 className="wg-subsection-heading">Viewing Deluge execution logs</h4>
							<ol className="wg-list wg-list--ordered">
								<li>Go to <strong>Zoho Projects &gt; Settings &gt; Developer Space &gt; Custom Functions</strong></li>
								<li>Click the function name</li>
								<li>Click <strong>Execution Logs</strong> to see recent invocations, response bodies, and errors</li>
							</ol>
						</section>

						{/* Section 8 — Rotating the Secret */}
						<section className="wg-section">
							<h3 className="wg-section-heading">
								<span className="wg-section-num">8</span>
								Rotating the Webhook Secret
							</h3>
							<ol className="wg-list wg-list--ordered">
								<li>
									<strong>Generate a new secret:</strong>
									<CodeBlock code={CODE_OPENSSL} language="bash" />
								</li>
								<li>
									<strong>Update ALL custom functions in Zoho Projects first:</strong> open each function
									(<code className="wg-inline-code">piaSyncTask</code>, <code className="wg-inline-code">piaSyncBug</code>,{" "}
									<code className="wg-inline-code">piaSyncTaskComment</code>, <code className="wg-inline-code">piaSyncBugComment</code>,{" "}
									<code className="wg-inline-code">piaSyncForumPost</code>), replace the <code className="wg-inline-code">bearerToken</code> value, and save.
								</li>
								<li>
									<strong>Update Cloudflare Worker:</strong>
									<CodeBlock code={CODE_WRANGLER_SECRET} language="bash" />
								</li>
								<li>
									<strong>Verify</strong> with a test task update — edit a task and check Worker logs (<code className="wg-inline-code">npx wrangler tail</code>).
								</li>
							</ol>
							<p className="wg-callout wg-callout--important">
								<strong>Important:</strong> Always update the Deluge functions <em>before</em> updating the Worker secret.
								Updating the functions first means the new token is ready in Zoho before the Worker starts requiring it,
								which minimises the failure window to near zero.
							</p>
						</section>

						{/* Section 9 — Entity Type Reference */}
						<section className="wg-section">
							<h3 className="wg-section-heading">
								<span className="wg-section-num">9</span>
								Entity Type Reference
							</h3>
							<GuideTable
								headers={["Entity Type", "Deluge entity_type Value", "Trigger Source", "Module"]}
								rows={[
									["Task (description + metadata)", "<code>task_description</code>", "Automation rule: Tasks", "Tasks"],
									["Task comment", "<code>task_comment</code>", "Automation rule: Tasks (comment trigger)", "Tasks"],
									["Bug/Issue", "<code>bug</code>", "Automation rule: Issues", "Issues (Bugs)"],
									["Bug comment", "<code>bug_comment</code>", "Automation rule: Issues (comment trigger)", "Issues (Bugs)"],
									["Forum post", "<code>forum_post</code>", "Automation rule: Forums (if available)", "Forums"],
									["Timesheet", "<code>timesheet</code>", "<strong>Not available</strong> — cron/manual sync only", "N/A"],
								]}
							/>

							<h4 className="wg-subsection-heading">Webhook Payload Schema</h4>
							<p className="wg-para">Every custom function sends the same JSON structure:</p>
							<CodeBlock code={CODE_PAYLOAD_SCHEMA} language="json" />
							<GuideTable
								headers={["Field", "Type", "Description"]}
								rows={[
									["<code>event_type</code>", "string", "Always <code>\"entity_updated\"</code> — PIA fetches the latest state regardless"],
									["<code>entity_type</code>", "string", "One of the values from the table above"],
									["<code>entity_id</code>", "string", "Zoho entity ID. For comments, this is the <strong>parent</strong> entity ID (task or bug)"],
									["<code>project_id</code>", "string", "Zoho Projects project ID"],
									["<code>portal_name</code>", "string", "Zoho portal/organisation name"],
									["<code>timestamp</code>", "string", "ISO 8601 UTC timestamp of when the webhook fired"],
									["<code>zoho_user_id</code>", "string", "Zoho user ID whose stored OAuth tokens PIA uses for API calls"],
								]}
							/>
						</section>

						{/* Section 10 — Known Limitations */}
						<section className="wg-section">
							<h3 className="wg-section-heading">
								<span className="wg-section-num">10</span>
								Known Limitations
							</h3>
							<GuideTable
								headers={["Limitation", "Detail", "Workaround"]}
								rows={[
									["Timesheet sync", "No automation rule triggers available for timesheets in Zoho Projects", "Nightly cron + manual sync only"],
									["Forum automation", "May not be available in all Zoho Projects plans", "Falls back to cron/manual sync"],
									["<code>get_organization_variable()</code>", "Not available in Zoho Projects Deluge (CRM-only)", "Config values hardcoded in each function body"],
									["Bearer token rotation", "Must update every custom function individually", "Documented in Section 8"],
									["OAuth token expiry", "Ingestion user's refresh token can expire if unused", "Periodically log in via PIA browser to refresh"],
									["Comment trigger availability", "\"Comment Added\" may not be a trigger option in all Zoho Projects editions", "Falls back to cron/manual sync for comments"],
									["Per-project setup", "Each Zoho Projects project needs its own set of automation rules", "Automation rules are project-scoped in Zoho Projects"],
									["<code>invokeurl</code> daily limit", "2,000 calls/day across the entire Zoho organisation", "Monitor via budget endpoint; reduce triggers if needed"],
								]}
							/>
						</section>

					</div>
				</div>
			</div>
		</div>
	);

	return createPortal(modalContent, document.body);
}
